/**
 * Unsaved-edit registry.
 *
 * Every editor that holds a draft registers a dirty check and a short description of what
 * would be lost. Navigation asks `confirmDiscardChanges()` before it moves; the browser's own
 * reload and close ask `hasUnsavedChanges()` through the `beforeunload` handler. The prompt
 * itself lives here so every surface asks the same question in the same words.
 */
export interface UnsavedEditor {
  /** True while the editor holds edits that are not saved. */
  isDirty: () => boolean;
  /** What the prompt names: "ticket overrides", "the Footprint routes". */
  what: string;
}

const editors = new Set<UnsavedEditor>();

/** Register an editor that may hold unsaved changes. Returns the unregister function. */
export function registerUnsavedEditor(editor: UnsavedEditor): () => void {
  editors.add(editor);
  return () => {
    editors.delete(editor);
  };
}

/** Descriptions of every editor that is dirty right now. */
export function unsavedEditors(): string[] {
  const dirty: string[] = [];
  for (const editor of editors) if (editor.isDirty()) dirty.push(editor.what);
  return dirty;
}

export function hasUnsavedChanges(): boolean {
  for (const editor of editors) if (editor.isDirty()) return true;
  return false;
}

/** The one question every surface asks. */
export function discardPrompt(what: readonly string[]): string {
  if (what.length === 0) return 'Discard unsaved changes?';
  if (what.length === 1) return `Discard unsaved changes to ${what[0]}?`;
  return `Discard unsaved changes to ${what.slice(0, -1).join(', ')} and ${what[what.length - 1]}?`;
}

/**
 * True when navigation may continue: nothing is dirty, or the operator agreed to discard.
 * `confirm` is injectable for tests; the default is the browser dialog.
 */
export function confirmDiscardChanges(
  confirm: (message: string) => boolean = (message) => window.confirm(message),
): boolean {
  const dirty = unsavedEditors();
  if (dirty.length === 0) return true;
  return confirm(discardPrompt(dirty));
}

/**
 * Warn on reload and tab close while something is dirty. Browsers show their own wording;
 * the returned string is ignored by modern ones but `preventDefault` is what matters.
 */
export function installUnloadGuard(target: Window = window): () => void {
  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = '';
  };
  target.addEventListener('beforeunload', onBeforeUnload);
  return () => target.removeEventListener('beforeunload', onBeforeUnload);
}
